import { NgClass, NgStyle } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'

import { TranslatePipe } from '@ngx-translate/core'
import { RecaptchaLoaderService, ReCaptchaV3Service } from 'ng-recaptcha'
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload'
import { PasswordModule } from 'primeng/password'
import { Textarea } from 'primeng/textarea'
import { finalize } from 'rxjs'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { InputComponent } from 'app/_shared/components/input/input.component'
import { ContactService } from 'app/_shared/services/contact.service'
import { ToastService } from 'app/_shared/services/toast.service'
import { AppConfigService } from 'app/app-config.service'

@Component({
  selector: 'app-contact',
  imports: [
    ButtonComponent,
    FileUploadModule,
    InputComponent,
    ReactiveFormsModule,
    TranslatePipe,
    Textarea,
    NgStyle,
    PasswordModule,
    NgClass
  ],
  providers: [ReCaptchaV3Service, RecaptchaLoaderService],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.scss'
})
export class ContactComponent implements OnInit {
  contactForm: FormGroup
  appConfigService: AppConfigService = inject(AppConfigService)
  recaptchaV3Service = inject(ReCaptchaV3Service)

  fileToUpload: File | null = null
  imagePreview: string | null = null
  storefrontId: number
  toastService = inject(ToastService)
  isLoadingSubmit = false

  constructor(
    private fb: FormBuilder,
    private contactService: ContactService
  ) {}

  ngOnInit(): void {
    this.storefrontId = this.appConfigService.getAppConfig?.id
    this.buildForm()

    const state = window.history.state
    if (state.initialFormValues) {
      this.contactForm.patchValue(state.initialFormValues)
    }
  }

  buildForm(): void {
    this.contactForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      message: ['', Validators.required],
      order_uuid: ['']
    })
  }

  getContactControl(controlName: string): FormControl {
    return this.contactForm.get(controlName) as FormControl
  }

  onFileSelect(event: FileSelectEvent): void {
    if (event.files && event.files.length > 0) {
      this.fileToUpload = event.files[0]
      // Read file as a data URL for preview
      const reader = new FileReader()
      reader.onload = () => {
        this.imagePreview = reader.result as string
      }
      reader.readAsDataURL(this.fileToUpload)
    }
  }

  removeImage(event?: Event): void {
    event?.stopPropagation()
    this.fileToUpload = null
    this.imagePreview = null
  }

  onSubmit(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched()
      return
    }
    this.recaptchaV3Service.execute('contact_submit').subscribe(token => {
      const formData = new FormData()
      formData.append('first_name', this.contactForm.get('first_name')?.value)
      formData.append('last_name', this.contactForm.get('last_name')?.value)
      formData.append('email', this.contactForm.get('email')?.value)
      formData.append('message', this.contactForm.get('message')?.value)
      formData.append('recaptcha', token)
      if (this.contactForm.get('order_uuid')?.value) {
        formData.append('order_uuid', this.contactForm.get('order_uuid')?.value)
      }
      if (this.fileToUpload) {
        formData.append('image', this.fileToUpload, this.fileToUpload.name)
      }

      this.isLoadingSubmit = true

      this.contactService
        .submitContact(this.storefrontId, formData)
        .pipe(finalize(() => (this.isLoadingSubmit = false)))
        .subscribe({
          next: () => {
            this.contactForm.reset()
            this.removeImage()
            this.toastService.success('Contact submission successful!')
          },
          error: error => {
            this.toastService.error(error.error?.message)
          }
        })
    })
  }
}
